// ================================================================
// CURRENT HOME PAGE — no courses or community (not yet ready)
// ----------------------------------------------------------------
// When courses & community are live, switch router.tsx to:
//   import { HomePage } from '@/pages/public/home-full';
// ================================================================

import { ArrowRight, BookOpen, CheckCircle2, Heart, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Turnstile, turnstileEnabled } from '@/components/turnstile';
import { Input, Label } from '@/components/ui/primitives';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

const COMMUNITY_ID = 'community-register';

const scrollToCommunity = () =>
  document.getElementById(COMMUNITY_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export function HomePage() {
  return (
    <>
      <Hero />
      <PromiseStrip />
      <AboutMe />
      <Pillars />
      <DevotionalCTA />
      <ComingSoon />
      <Testimonials />
      <ClosingCTA />
    </>
  );
}

/* ---------------------------------------------------------------- */
/* Hero                                                              */
/* ---------------------------------------------------------------- */
function Hero() {
  return (
    <section className="grain relative isolate overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-24 -right-20 h-[520px] w-[520px] rounded-full bg-terracotta-200/50 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-20 h-[520px] w-[520px] rounded-full bg-sage-200/60 blur-3xl"
      />

      <div className="container-wide relative grid gap-16 pb-28 pt-16 md:pb-40 md:pt-24 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7 lg:pt-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-primary" />
            <span className="eyebrow">A Christian sisterhood</span>
          </div>

          <h1 className="mt-8 font-display text-5xl leading-[0.95] tracking-tight text-balance md:text-7xl lg:text-[5.5rem]">
            From <em className="italic text-terracotta-600">broken</em>
            <br />
            to{' '}
            <span className="relative inline-block">
              blessed
              <svg
                aria-hidden
                viewBox="0 0 240 20"
                className="absolute -bottom-2 left-0 w-full text-accent"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 14 Q 60 4, 120 10 T 238 8"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            <span className="block text-muted-foreground">— in the Potter's hands.</span>
          </h1>

          <p className="mt-8 max-w-xl text-lg leading-relaxed text-foreground/80 text-pretty">
            A faith-rooted community for women rebuilding their lives after divorce. Courses,
            devotionals, and a sisterhood that walks with you — you don't have to face this
            valley alone.
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="xl" className="group">
              <Link to="/devotional">
                Free 7-day devotional
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button size="xl" variant="outline" onClick={scrollToCommunity}>
              Register your interest
            </Button>
          </div>

          <div className="mt-14 flex items-center gap-6">
            <SisterAvatars />
            <p className="mt-1 text-sm text-muted-foreground">
              Women on their healing journey
            </p>
          </div>
        </div>

        <div className="relative lg:col-span-5">
          <EditorialCollage />
        </div>
      </div>

      <div className="relative border-y border-ink/10 bg-cream-100/60 py-5 backdrop-blur-sm">
        <div className="container-wide flex items-center justify-center gap-4 text-center">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden />
          <p className="font-display text-base italic text-foreground/90 md:text-lg">
            "The Lord is close to the brokenhearted and saves those who are crushed in spirit."
            <span className="ml-2 text-xs not-italic uppercase tracking-[0.2em] text-muted-foreground">
              Psalm 34:18
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}

function SisterAvatars() {
  const colors = ['#c46856', '#677e5b', '#cfa762', '#b35543', '#506347'];
  return (
    <div className="flex -space-x-3">
      {colors.map((c, i) => (
        <div
          key={c}
          className="h-11 w-11 rounded-full border-[3px] border-background"
          style={{ background: `linear-gradient(135deg, ${c} 0%, ${c}dd 100%)` }}
          aria-hidden
        >
          <div className="flex h-full w-full items-center justify-center font-display text-sm font-semibold text-white">
            {String.fromCharCode(65 + i)}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditorialCollage() {
  return (
    <div className="relative mx-auto h-[560px] w-full max-w-md lg:h-[620px]">
      <div className="absolute right-4 top-8 w-[78%] rotate-[3deg] overflow-hidden rounded-2xl border border-ink/10 bg-card shadow-2xl shadow-terracotta-900/10">
        <div className="aspect-[4/5] relative">
          <div className="absolute inset-0 bg-gradient-to-br from-terracotta-300 via-terracotta-400 to-terracotta-600" />
          <div className="absolute inset-0 bg-grain opacity-30 mix-blend-overlay" />
          <svg
            viewBox="0 0 200 250"
            className="absolute inset-0 h-full w-full text-white/90"
            fill="none"
            aria-hidden
          >
            <path d="M85 40h30v60h60v30h-60v90h-30v-90H25v-30h60V40z" fill="currentColor" opacity="0.92" />
            {[...Array(6)].map((_, i) => (
              <line
                key={i}
                x1="100" y1="115"
                x2={100 + Math.cos((i * Math.PI) / 3) * 60}
                y2={115 + Math.sin((i * Math.PI) / 3) * 60}
                stroke="currentColor" strokeWidth="1.5" opacity="0.3"
              />
            ))}
            <path d="M40 200 Q 55 170, 85 185" stroke="hsl(103, 18%, 75%)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            <path d="M160 200 Q 145 170, 115 185" stroke="hsl(103, 18%, 75%)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          </svg>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Gracefully Rebuilt</div>
            <div className="mt-1 font-display text-xl leading-tight text-white">From Broken to Blessed</div>
          </div>
        </div>
      </div>

      <div className="absolute left-0 top-0 w-[60%] -rotate-[5deg] rounded-xl border border-ink/10 bg-card p-5 shadow-xl">
        <div className="eyebrow text-terracotta-600">Daily Devotional</div>
        <p className="mt-3 font-display text-lg italic leading-snug">
          "For I know the plans I have for you," declares the Lord…
        </p>
        <div className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">Jeremiah 29:11</div>
      </div>

      <div className="absolute bottom-4 left-0 w-[65%] rotate-[2deg] rounded-xl border border-ink/10 bg-card p-5 shadow-xl">
        <div className="eyebrow text-sage-700">Day 1 of 7</div>
        <p className="mt-2 text-sm leading-relaxed text-foreground/80">
          Embracing God's love amidst the pain. He is close to the brokenhearted. 🤍
        </p>
        <div className="mt-3 w-full overflow-hidden rounded-full bg-sage-100 h-1.5">
          <div className="h-full w-[14%] rounded-full bg-sage-500" />
        </div>
      </div>

      <div aria-hidden className="absolute right-0 bottom-24 h-20 w-20 rotate-12 rounded-full border-2 border-dashed border-accent/50" />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Promise strip                                                     */
/* ---------------------------------------------------------------- */
function PromiseStrip() {
  const items = [
    'Biblically rooted teaching',
    'A safe sisterhood',
    'Healing at your pace',
    'Real women, real stories',
  ];
  return (
    <section className="border-b border-ink/10">
      <div className="container-wide flex flex-wrap items-center justify-center gap-x-10 gap-y-4 py-6 text-sm text-muted-foreground">
        {items.map((item, i) => (
          <div key={item} className="flex items-center gap-6">
            <span className="font-medium text-foreground/80">{item}</span>
            {i < items.length - 1 && <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* About Me                                                          */
/* ---------------------------------------------------------------- */
function AboutMe() {
  return (
    <section className="container-wide py-28 md:py-40">
      <div className="grid gap-16 lg:grid-cols-2 lg:items-center lg:gap-24">

        {/* Photo — framed */}
        <div className="relative flex justify-center lg:order-last">
          {/* decorative layers behind the photo */}
          <div
            aria-hidden
            className="absolute inset-0 translate-x-4 translate-y-4 rounded-3xl bg-terracotta-100"
          />
          <div
            aria-hidden
            className="absolute inset-0 -translate-x-2 translate-y-2 rounded-3xl border border-terracotta-200"
          />
          {/* photo */}
          <div className="relative w-full max-w-sm overflow-hidden rounded-3xl border-4 border-white shadow-2xl shadow-terracotta-200/60">
            <img
              src="/founder.jpg"
              alt="Founder of Gracefully Rebuilt"
              className="aspect-square w-full object-cover object-top"
            />
            {/* subtle gradient at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-terracotta-900/20 to-transparent" />
          </div>
          {/* decorative quote badge */}
          <div className="absolute -bottom-6 -left-4 max-w-[200px] rounded-2xl border border-ink/10 bg-white p-4 shadow-xl">
            <p className="font-display text-sm italic leading-snug text-foreground/80">
              "Beauty can rise from brokenness."
            </p>
          </div>
        </div>

        {/* Copy */}
        <div>
          <div className="eyebrow">Welcome</div>
          <h2 className="mt-5 font-display text-4xl leading-[1.05] tracking-tight md:text-5xl">
            Welcome to{' '}
            <span className="italic text-terracotta-600">Gracefully Rebuilt.</span>
          </h2>

          <div className="mt-8 space-y-5 text-lg leading-relaxed text-foreground/75">
            <p>
              This space is being created for women who are walking through heartbreak, divorce,
              separation, or any season that has left them feeling broken, lost, or unsure of what
              comes next. It will be a place of healing, hope, and gentle rebuilding — a community
              where women will be reminded that their story is not over.
            </p>
            <p>
              Gracefully Rebuilt is born out of a deeply personal journey of healing, surrender,
              and rediscovery. What once felt like the end became the beginning of a new chapter,
              and through it, I want to share the truth that beauty can rise from brokenness.
            </p>
            <p>
              My hope is that this community will become a safe space where women feel seen,
              encouraged, and supported as they rebuild their lives with faith, courage, and grace.
            </p>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-ink/10" />
            <p className="font-display text-base italic text-muted-foreground">
              With love &amp; faith
            </p>
            <div className="h-px flex-1 bg-ink/10" />
          </div>
        </div>

      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Four pillars                                                      */
/* ---------------------------------------------------------------- */
function Pillars() {
  const pillars = [
    {
      eyebrow: 'Pillar 01',
      title: 'Healing the heart',
      copy: 'Devotionals and reflection that meet you where the pain still lives — scripture, prayer, and honest space to grieve and grow.',
      icon: Heart,
    },
    {
      eyebrow: 'Pillar 02',
      title: 'Rebuilding identity',
      copy: 'Faith-based teaching that walks you from "who am I now?" to rooted confidence in who God says you are.',
      icon: BookOpen,
    },
    {
      eyebrow: 'Pillar 03',
      title: 'A real sisterhood',
      copy: 'Women who truly get it — praying, encouraging, and walking beside you. Community built on faith and shared experience.',
      icon: Sparkles,
    },
    {
      eyebrow: 'Pillar 04',
      title: 'Whole-life stewardship',
      copy: 'Guidance on co-parenting, finances, and the daily work of rebuilding — all rooted in His wisdom and grace.',
      icon: Sparkles,
    },
  ];

  return (
    <section className="container-wide py-28 md:py-40">
      <div className="mx-auto max-w-3xl text-center">
        <div className="eyebrow">Our four pillars</div>
        <h2 className="mt-5 font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
          A whole woman, gently rebuilt.
        </h2>
        <p className="mt-6 text-pretty text-lg text-foreground/70">
          Divorce touches everything — your faith, your mothering, your mirror, your money. We walk
          with you through all of it.
        </p>
      </div>

      <div className="mt-20 grid gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-4">
        {pillars.map((p, i) => (
          <div
            key={p.title}
            className={cn(
              'group relative flex flex-col',
              i % 2 === 1 && 'lg:mt-16',
              i === 2 && 'md:mt-12 lg:mt-0',
              i === 3 && 'md:mt-0 lg:mt-16',
            )}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-terracotta-100 text-terracotta-700 transition-all group-hover:bg-terracotta-600 group-hover:text-white">
              <p.icon className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div className="eyebrow mt-6">{p.eyebrow}</div>
            <h3 className="mt-3 font-display text-2xl leading-tight">{p.title}</h3>
            <p className="mt-3 text-pretty text-foreground/70 leading-relaxed">{p.copy}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Devotional CTA                                                    */
/* ---------------------------------------------------------------- */
function DevotionalCTA() {
  return (
    <section className="container-wide py-28 md:py-40">
      <div className="relative overflow-hidden rounded-3xl border border-ink/10 bg-sage-50 dark:bg-sage-900/20">
        <div aria-hidden className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-sage-200/70 blur-3xl" />
        <div className="relative grid gap-10 p-10 md:p-16 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="eyebrow text-sage-700">Free — start today</div>
            <h2 className="mt-5 font-display text-4xl leading-tight md:text-5xl">
              7 days of scripture to steady your soul.
            </h2>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-foreground/75">
              A gentle first step. Delivered to your inbox — one devotional a day, with scripture,
              reflection, and prayer. No strings attached.
            </p>
            <div className="mt-8">
              <Button asChild size="xl" className="group">
                <Link to="/devotional">
                  Get the free devotional
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              No spam. Unsubscribe anytime. Your email stays between us.
            </p>
          </div>

          <div className="relative flex justify-center">
            <div className="relative h-[340px] w-[260px]">
              <div className="absolute inset-0 translate-x-3 translate-y-3 rotate-[4deg] rounded-lg bg-cream-100 shadow-lg" />
              <div className="absolute inset-0 -translate-x-2 translate-y-1 -rotate-[3deg] rounded-lg bg-cream-200 shadow-lg" />
              <div className="absolute inset-0 rounded-lg border border-ink/10 bg-card p-7 shadow-xl">
                <div className="eyebrow text-terracotta-600">Day 01</div>
                <h3 className="mt-2 font-display text-2xl leading-tight">
                  Embracing God's love amidst the pain.
                </h3>
                <div className="mt-5 h-px w-10 bg-ink/20" />
                <p className="mt-4 font-display italic leading-snug text-foreground/80">
                  In moments of deep pain, it's easy to feel abandoned. But Scripture reminds us
                  God is especially near to those who are hurting.
                </p>
                <div className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">Psalm 34:18</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Coming soon — register interest form                             */
/* ---------------------------------------------------------------- */
function ComingSoon() {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — real people never see it
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (turnstileEnabled && !captchaToken) {
      setError('Please complete the quick security check.');
      return;
    }
    setSubmitting(true);
    try {
      // Goes through an Edge Function that verifies the bot check server-side.
      const { data, error: fnError } = await supabase.functions.invoke('join-waitlist', {
        body: {
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          token: captchaToken,
          website,
        },
      });
      if (fnError) throw fnError;

      if (data?.status === 'ok') {
        setSubmitted(true);
      } else if (data?.status === 'duplicate') {
        setError("You're already on the list — we'll be in touch! 🤍");
      } else {
        setError(data?.message ?? 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      // Turnstile tokens are single-use — refresh for any retry
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
    }
  };

  const upcoming = [
    {
      icon: BookOpen,
      label: 'Courses',
      title: 'Faith-based courses',
      body: 'Six-week courses on healing, identity, parenting, finances, and relationships — all rooted in Scripture.',
    },
    {
      icon: Heart,
      label: 'Community',
      title: 'The Sisterhood',
      body: 'A private community of women who truly understand. Real conversations, real support, no judgment.',
    },
  ];

  return (
    <section id={COMMUNITY_ID} className="relative bg-ink py-28 text-cream-50 md:py-40">
      <div aria-hidden className="absolute inset-0 bg-grain opacity-[0.04]" />
      <div className="container-wide relative">

        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-cream-200/20 bg-cream-50/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-cream-200/80">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-terracotta-300 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-terracotta-300" />
            </span>
            Coming soon
          </div>
          <h2 className="mt-6 font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
            More is on the way.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-cream-200/80">
            We're building something beautiful. Register your interest and you'll be the first to
            know when it opens.
          </p>
        </div>

        {/* Feature cards */}
        <div className="mx-auto mt-16 grid max-w-3xl gap-6 md:grid-cols-2">
          {upcoming.map((item) => (
            <div key={item.title} className="rounded-2xl border border-cream-200/10 bg-cream-50/5 p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta-400/20 text-terracotta-300">
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta-400">
                {item.label}
              </div>
              <h3 className="mt-2 font-display text-2xl text-cream-50">{item.title}</h3>
              <p className="mt-3 leading-relaxed text-cream-200/70">{item.body}</p>
            </div>
          ))}
        </div>

        {/* Register interest form */}
        <div className="mx-auto mt-16 max-w-lg">
          <div className="rounded-3xl border border-cream-200/15 bg-cream-50/8 p-8 backdrop-blur-sm md:p-10">
            {submitted ? (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage-500/20">
                  <CheckCircle2 className="h-7 w-7 text-sage-300" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl text-cream-50">
                  You're on the list{firstName ? `, ${firstName.split(' ')[0]}` : ''}! 🤍
                </h3>
                <p className="text-cream-200/70">
                  We'll reach out the moment the community opens. Thank you for believing in this
                  before it even begins.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-2xl text-cream-50">Register your interest</h3>
                <p className="mt-2 text-sm text-cream-200/60">
                  Be the first to know when courses and community open.
                </p>
                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {/* Honeypot — hidden from real visitors, bots fill it in */}
                  <input
                    type="text"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1}
                    aria-hidden="true"
                    autoComplete="off"
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="ci-firstName" className="text-cream-200/80">First name</Label>
                      <Input
                        id="ci-firstName"
                        placeholder="Sarah"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="border-cream-200/20 bg-cream-50/10 text-cream-50 placeholder:text-cream-200/30 focus:ring-terracotta-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ci-email" className="text-cream-200/80">Email address</Label>
                      <Input
                        id="ci-email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border-cream-200/20 bg-cream-50/10 text-cream-50 placeholder:text-cream-200/30 focus:ring-terracotta-400"
                      />
                    </div>
                  </div>

                  <Turnstile key={captchaKey} onToken={setCaptchaToken} theme="dark" />

                  {error && (
                    <p className="rounded-xl border border-terracotta-400/30 bg-terracotta-400/10 px-4 py-3 text-sm text-terracotta-200">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full bg-terracotta-400 text-ink hover:bg-terracotta-300"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {submitting ? 'Saving…' : 'Count me in'}
                  </Button>

                  <p className="text-center text-xs text-cream-200/40">
                    No spam. We'll only contact you when we're ready to open.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Testimonials                                                      */
/* ---------------------------------------------------------------- */
function Testimonials() {
  const quotes = [
    {
      quote: "I thought I would never feel whole again. The devotional was the first thing that made me believe God hadn't abandoned me.",
      name: 'Rebecca A.',
      meta: 'Divorced 2 years · mom of three',
    },
    {
      quote: "The community of women here pray for you at 2am. I didn't know I needed that until I found it. Actual Christ-centered support.",
      name: 'Janelle M.',
      meta: 'Recently separated',
    },
    {
      quote: "This isn't just about divorce — it's a reconstruction of self, rooted in scripture. It changed how I see God's plan for my life.",
      name: 'Elena T.',
      meta: 'Divorced 5 years',
    },
  ];

  return (
    <section className="container-wide py-28 md:py-40">
      <div className="mx-auto max-w-2xl text-center">
        <div className="eyebrow">Voices from the sisterhood</div>
        <h2 className="mt-5 font-display text-4xl leading-tight md:text-6xl">
          Women who walked this road.
        </h2>
      </div>

      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {quotes.map((q, i) => (
          <figure
            key={q.name}
            className={cn(
              'relative flex flex-col rounded-2xl border border-ink/10 bg-card p-8 shadow-sm',
              i === 1 && 'md:-translate-y-6',
            )}
          >
            <svg aria-hidden viewBox="0 0 40 40" className="h-8 w-8 text-terracotta-300" fill="currentColor">
              <path d="M13 10c-4 0-7 3-7 7 0 4 3 7 7 7 .5 0 1 0 1.5-.2-.8 2.4-2.7 4-5.5 5l1 2c5.6-1.5 9-5.8 9-11v-2c0-4.3-2.7-7-6-7zm17 0c-4 0-7 3-7 7 0 4 3 7 7 7 .5 0 1 0 1.5-.2-.8 2.4-2.7 4-5.5 5l1 2c5.6-1.5 9-5.8 9-11v-2c0-4.3-2.7-7-6-7z" />
            </svg>
            <blockquote className="mt-5 flex-1 font-display text-xl leading-snug text-foreground/90">
              {q.quote}
            </blockquote>
            <figcaption className="mt-6 border-t border-ink/10 pt-5">
              <div className="font-medium">{q.name}</div>
              <div className="text-sm text-muted-foreground">{q.meta}</div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Closing CTA                                                       */
/* ---------------------------------------------------------------- */
function ClosingCTA() {
  return (
    <section className="container-wide pb-28 md:pb-40">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-terracotta-500 via-terracotta-600 to-terracotta-700 p-12 text-cream-50 md:p-20">
        <div aria-hidden className="absolute inset-0 bg-grain opacity-10 mix-blend-overlay" />
        <div aria-hidden className="absolute -right-16 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-cream-50/10 blur-3xl" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="font-display text-5xl leading-[1.02] tracking-tight md:text-7xl">
            Your next chapter is already being written.
          </h2>
          <p className="mx-auto mt-7 max-w-xl text-lg leading-relaxed text-cream-100/90">
            Start with the free 7-day devotional — or register your interest and be first inside
            when the community opens.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="xl" className="bg-cream-50 text-terracotta-700 hover:bg-cream-100 shadow-lg">
              <Link to="/devotional">
                Get the free devotional
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <button
              type="button"
              onClick={scrollToCommunity}
              className="text-sm font-medium text-cream-100/80 underline-offset-4 hover:underline"
            >
              Register your interest →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
